import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ServerError from "./pages/ServerError";

// Solution Pages
import Publishers from "./pages/solutions/Publishers";
import Agencies from "./pages/solutions/Agencies";
import Brands from "./pages/solutions/Brands";
import Fashion from "./pages/solutions/Fashion";
import TravelAndTourism from "./pages/solutions/TravelAndTourism";
import Automotive from "./pages/solutions/Automotive";
import EntertainmentAndMedia from "./pages/solutions/EntertainmentAndMedia";
import ConsumerElectronics from "./pages/solutions/ConsumerElectronics";
import FinancialServices from "./pages/solutions/FinancialServices";

// Other Pages
import Demo from "./pages/Demo";
import Gallery from "./pages/Gallery";
import GalleryPreview from "./pages/GalleryPreview";
import Platform from "./pages/Platform";
import About from "./pages/About";
import Resources from "./pages/Resources";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import JobApplication from "./pages/JobApplication";
import Terms from "./pages/Terms";

// Product Pages
import ManagedServicesPage from "./pages/ManagedServicesPage";

// Temporary test pages (re-enable by uncommenting when needed)
// import HubSpotTest from "./pages/HubSpotTest";
// import MondayTest from "./pages/MondayTest";

// Resource Sub-pages
import Legal from "./pages/resources/Legal";
import Templates from "./pages/resources/Templates";
import Blog from "./pages/Blog";
import BlogPost from "./pages/resources/BlogPost";
import GalleryShowcase from "./pages/resources/GalleryShowcase";
import InAppGallery from "./pages/embed/InAppGallery";

const queryClient = new QueryClient();

// Redirect /posts/* to /blog/*
const PostsRedirect = () => {
  const params = window.location.pathname.replace(/^\/posts\/?/, '');
  return <Navigate to={`/blog/${params}`} replace />;
};

// Redirect /ad-gallery/:slug to /gallery/preview?item=:slug
const AdGalleryRedirect = () => {
  const slug = window.location.pathname.replace(/^\/ad-gallery\//, '').replace(/\/$/, '');
  return <Navigate to={`/gallery/preview?item=${encodeURIComponent(slug)}`} replace />;
};

const App = () => (
  <ThemeProvider defaultTheme="dark">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <div id="main-content">
          <Routes>
            <Route path="/" element={<Index />} />

            {/* Solutions */}
            <Route path="/solutions/publishers" element={<Publishers />} />
            <Route path="/solutions/agencies" element={<Agencies />} />
            <Route path="/solutions/brands" element={<Brands />} />
            <Route path="/solutions/fashion" element={<Fashion />} />
            <Route path="/solutions/travel-and-tourism" element={<TravelAndTourism />} />
            <Route path="/solutions/automotive" element={<Automotive />} />
            <Route path="/solutions/entertainment-and-media" element={<EntertainmentAndMedia />} />
            <Route path="/solutions/consumer-electronics" element={<ConsumerElectronics />} />
            <Route path="/solutions/financial-services" element={<FinancialServices />} />

            {/* Legal Pages */}
            <Route path="/legal" element={<Navigate to="/resources/legal" replace />} />
            <Route path="/legal/:slug" element={<Legal />} />

            {/* Main Pages */}
            <Route path="/demo" element={<Demo />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/gallery/preview" element={<GalleryPreview />} />
            <Route path="/embed/in-app-gallery" element={<InAppGallery />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/about" element={<About />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/apply" element={<JobApplication />} />
            <Route path="/privacy" element={<Navigate to="/legal/privacy-policy" replace />} />
            <Route path="/terms" element={<Terms />} />

            {/* Product Pages */}
            <Route path="/managed-services" element={<ManagedServicesPage />} />

            {/* Blog */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Old site redirects */}
            <Route path="/posts" element={<Navigate to="/blog" replace />} />
            <Route path="/posts/*" element={<PostsRedirect />} />
            <Route path="/create" element={<Navigate to="/platform" replace />} />
            <Route path="/ad-tag-generator" element={<Navigate to="/platform" replace />} />
            <Route path="/html5-ad-gallery" element={<Navigate to="/gallery" replace />} />
            <Route path="/html5-ad-gallery/*" element={<Navigate to="/gallery" replace />} />
            <Route path="/ad-gallery" element={<Navigate to="/gallery" replace />} />
            <Route path="/ad-gallery/*" element={<AdGalleryRedirect />} />

            {/* Resource Sub-pages */}
            <Route path="/resources/legal" element={<Legal />} />
            <Route path="/resources/templates" element={<Templates />} />
            <Route path="/resources/gallery" element={<GalleryShowcase />} />

            {/* Temporary test routes — re-enable by uncommenting import + route when needed */}
            {/* <Route path="/hubspot-test" element={<HubSpotTest />} /> */}
            {/* <Route path="/monday-test" element={<MondayTest />} /> */}

            <Route path="/500" element={<ServerError />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </div>
          <CookieConsent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
